import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { MediaPermissionState } from '../models/device.model'
import { setPreferredSpeaker } from '../services/audio-output.service'
import { isPhone } from '../services/device-capability'
import {
  AUDIO_PRESETS,
  DEFAULT_AUDIO_PRESET,
  matchAudioPreset,
  type AudioFormat,
  type AudioPresetName,
  type AudioProcessing,
  type OpusOptions,
} from '../constants/audio-presets'

export { AUDIO_PRESETS, DEFAULT_AUDIO_PRESET } from '../constants/audio-presets'
export type {
  AudioFormat,
  AudioPreset,
  AudioPresetName,
  AudioProcessing,
  OpusOptions,
} from '../constants/audio-presets'

type DevicesState = {
  selectedMicId: string
  selectedCamId: string
  selectedSpeakerId: string
  audioProcessing: AudioProcessing
  /**
   * RNNoise-based noise cancellation. Distinct from the browser's
   * `noiseSuppression`: that is whatever the platform's own filter does, this
   * is a neural denoiser to be run over the captured audio ourselves.
   *
   * Kept out of `AudioProcessing` on purpose. That object is spread into
   * getUserMedia, and a key no browser defines would be passed down as a
   * phantom constraint.
   *
   * On by default on the web, where it runs in an AudioWorklet and the machine
   * can afford it. Mobile leaves it off: native mobile noise suppression is
   * already hardware-accelerated, a WASM denoiser costs battery, and running
   * one in series with the platform's own AEC tends to hurt more than it helps.
   */
  noiseCancellation: boolean
  /**
   * The named preset these audio settings correspond to, or `'custom'` once
   * they no longer match one.
   *
   * Derived, never authoritative: the individual switches stay editable, and a
   * panel that keeps saying "Music audio" after you turn echo cancellation back
   * on is describing audio you are not sending.
   */
  audioPreset: AudioPresetName | 'custom'
  /** Capture format. Real getUserMedia constraints. */
  audioFormat: AudioFormat
  /**
   * Opus encoder options, applied as `codecOptions` when the producer is made.
   *
   * Unlike the constraints above, these CANNOT be changed on a running track —
   * `codecOptions` is read once at produce time — so a change here reaches the
   * room on the next producer.
   */
  opus: OpusOptions
  videoQuality: VideoQuality
  /** Frames per second requested when sharing a screen. */
  screenShareFrameRate: number
  /**
   * Camera frame rate, overriding the quality preset's own.
   *
   * The preset carries an fps because resolution and frame rate together
   * decide the encode cost. This lets someone keep 720p and drop to 15 for a
   * slow link, or push 60 for motion. `null` means "whatever the preset says",
   * so the two controls do not silently fight over the same constraint.
   */
  webcamFrameRate: number | null
  /**
   * Capture aspect ratio, as width/height. The preset fixes both dimensions,
   * so this recomputes the height from the preset's width.
   */
  aspectRatio: number
  micEnabled: boolean
  camEnabled: boolean
  permissionState: MediaPermissionState
  /**
   * What the browser has actually said about each device, kept per kind.
   *
   * `permissionState` above is one field for two devices, and it was written in
   * three places and read in none — which is how a refused camera could leave
   * `camEnabled` sitting at its `true` default, so the lobby drew a lit camera
   * button over a dead preview and the meeting inherited it through VcApp.
   *
   * Per kind because the two answers genuinely differ: Firefox's prompt lets
   * you grant one and refuse the other, and a machine with no camera reports
   * `error` for video while the microphone is fine.
   */
  micPermission: MediaPermissionState
  camPermission: MediaPermissionState
  /**
   * Bumped when a device goes from REFUSED to granted.
   *
   * The meeting builds its producers once, in an effect keyed on the transport
   * — so a capture refused at join leaves no producer to resume, and a grant
   * given later would light the button with nothing publishing behind it.
   * `useLocalMedia` watches this so that one case, and only that one, rebuilds.
   * A first grant does not bump it: nothing was missed.
   */
  mediaRetryToken: number
}

type DevicesActions = {
  selectMic: (id: string) => void
  selectCam: (id: string) => void
  selectSpeaker: (id: string) => void
  setMicEnabled: (enabled: boolean) => void
  setCamEnabled: (enabled: boolean) => void
  /**
   * Back to the default mic/camera choice for the next person in this tab — on
   * sign-out. A device the browser is refusing stays off.
   */
  resetMediaChoice: () => void
  setPermissionState: (state: MediaPermissionState) => void
  /** Record the browser's verdict for one device. See `micPermission`. */
  setDevicePermission: (kind: 'audio' | 'video', state: MediaPermissionState) => void
  setDefaultsIfEmpty: (devices: MediaDeviceInfo[]) => void
  setAudioProcessing: (patch: Partial<AudioProcessing>) => void
  setNoiseCancellation: (enabled: boolean) => void
  selectAudioPreset: (name: AudioPresetName) => void
  setAudioFormat: (patch: Partial<AudioFormat>) => void
  setVideoQuality: (quality: VideoQuality) => void
  setScreenShareFrameRate: (fps: number) => void
  setWebcamFrameRate: (fps: number | null) => void
  setAspectRatio: (ratio: number) => void
}

/**
 * Capture presets behind the Settings quality dropdown, which was five menu
 * entries writing to a local variable nothing read.
 *
 * Resolution and frame rate can be changed on a running track, so switching
 * quality mid-call re-applies constraints rather than republishing.
 *
 * `maxBitrate` no longer reaches the encoder. It did when the ladder derived
 * every rung from it by division; rungs are absolute now (encoding-ladder.ts)
 * and nothing reads this field at produce time. `width` is the field that
 * matters: it decides which simulcast profile the capture lands in.
 */
export type VideoQuality = 'Low' | 'Medium' | 'High' | 'HD (1080p)' | 'Full HD (2160)'

export const VIDEO_QUALITY_PRESETS: Record<
  VideoQuality,
  { width: number; height: number; frameRate: number; maxBitrate: number }
> = {
  // One preset per simulcast profile, in order.
  //
  // `getEncodings` picks a ladder by the capture's long edge, taking the
  // smallest profile that can carry it (encoding-ladder.ts `chooseProfile`).
  // The five profiles are keyed 320 / 640 / 1280 / 1920 / 3840, and these five
  // widths now land on one each, so every option selects a distinct ladder.
  //
  // Before this they did not line up: High captured 960 wide, which rounds up
  // into the 1280 profile and gave it the same three rungs as the next option up, while
  // the 3840 profile was unreachable because no preset ever asked for a capture
  // that large.
  //
  // `maxBitrate` here no longer reaches the encoder — rungs carry their own
  // absolute bitrates and nothing reads this field at produce time. It is kept
  // aligned with each profile's top rung so the two cannot be read as
  // disagreeing; see the note above.
  Low: { width: 320, height: 180, frameRate: 15, maxBitrate: 150_000 },
  Medium: { width: 640, height: 360, frameRate: 30, maxBitrate: 500_000 },
  High: { width: 1280, height: 720, frameRate: 30, maxBitrate: 1_200_000 },
  'HD (1080p)': { width: 1920, height: 1080, frameRate: 30, maxBitrate: 3_500_000 },
  'Full HD (2160)': { width: 3840, height: 2160, frameRate: 30, maxBitrate: 10_000_000 },
}

const INITIAL: DevicesState = {
  selectedMicId: '',
  selectedCamId: '',
  selectedSpeakerId: '',
  // From the default preset, so there is one source for these numbers rather
  // than a copy here that can drift from the one the encoder reads.
  audioProcessing: { ...AUDIO_PRESETS[DEFAULT_AUDIO_PRESET].processing },
  noiseCancellation: AUDIO_PRESETS[DEFAULT_AUDIO_PRESET].noiseCancellation,
  audioPreset: DEFAULT_AUDIO_PRESET,
  audioFormat: { ...AUDIO_PRESETS[DEFAULT_AUDIO_PRESET].format },
  opus: { ...AUDIO_PRESETS[DEFAULT_AUDIO_PRESET].opus },
  // 'High' is 1280x720 — the capture the old 'HD (720p)' default produced.
  // Following the label instead would have moved every new user to a 1920
  // capture by renaming a constant, which is not a default change anyone
  // asked for.
  videoQuality: 'High',
  // 30, not 15.
  //
  // 15 was chosen because slides and documents are most of what gets shared and
  // cost a fraction of the bitrate at 15. That reasoning is sound for a static
  // slide and wrong for anything that moves, and until now it was not really a
  // default at all: the Settings dropdown wrote to local component state and
  // never reached this store, so every share on the platform was pinned to 15
  // whatever the user picked.
  //
  // Measured 2026-09-08 on a live share to an H.323 endpoint: 14.7 real frames
  // per second arriving at the mixer. The transcoder was emitting a correct
  // 30 fps at 984 kbps, but half of those frames were duplicates ffmpeg
  // inserted to fill -r 30, and a duplicated frame carries no motion. Nothing
  // downstream can recover frames the capture never took, which is why bitrate,
  // GOP, encoder preset, adaptive quantisation and jitter buffering all
  // improved artefacts and stability on that path and none of them touched
  // smoothness.
  //
  // The trade is real: at a fixed bitrate, twice the frames means half the bits
  // each. Someone sharing a static document should choose 15 in Settings — and
  // now that the control is wired to this store, that choice will take effect.
  screenShareFrameRate: 30,
  webcamFrameRate: null,
  aspectRatio: 16 / 9,
  micEnabled: true,
  camEnabled: true,
  permissionState: 'prompt',
  // 'prompt' is the honest starting answer and the one that lets the lobby ask.
  // It becomes 'granted' or 'denied' the first time a capture settles.
  micPermission: 'prompt',
  camPermission: 'prompt',
  mediaRetryToken: 0,
}

export const useDevicesStore = create<DevicesState & DevicesActions>()(
  persist(
    (set) => ({
    ...INITIAL,
    selectMic: (id) => set({ selectedMicId: id }),
    selectCam: (id) => set({ selectedCamId: id }),
    selectSpeaker: (id) => {
      // Mirrored to the module-level output helper so sounds played outside React
      // (notifications, the recording announcement) reach the same device.
      setPreferredSpeaker(id)
      set({ selectedSpeakerId: id })
    },
    setMicEnabled: (enabled) => set({ micEnabled: enabled }),
    setCamEnabled: (enabled) => set({ camEnabled: enabled }),
    resetMediaChoice: () =>
      set((prev) => ({
        micEnabled: INITIAL.micEnabled && prev.micPermission !== 'denied' && prev.micPermission !== 'error',
        camEnabled: INITIAL.camEnabled && prev.camPermission !== 'denied' && prev.camPermission !== 'error',
      })),
    setPermissionState: (state) => set({ permissionState: state }),
    /**
     * The one place that decides a refused device is off.
     *
     * Doing it here rather than at each toggle is what keeps the lobby preview,
     * the control bar and the published producer from disagreeing: they all
     * read `micEnabled`/`camEnabled`, and there is now no path that leaves one
     * of them `true` while the browser is refusing the device.
     */
    setDevicePermission: (kind, state) =>
      set((prev) => {
        const previous = kind === 'audio' ? prev.micPermission : prev.camPermission
        // A device we are not allowed to open cannot be "on". `error` counts:
        // a camera that is not there is not one you are sending.
        const off = state === 'denied' || state === 'error'
        // Recovering from a refusal is the only transition the meeting has to
        // rebuild for — see `mediaRetryToken`.
        const recovered = state === 'granted' && (previous === 'denied' || previous === 'error')
        const patch: Partial<DevicesState> =
          kind === 'audio'
            ? { micPermission: state, ...(off ? { micEnabled: false } : {}) }
            : { camPermission: state, ...(off ? { camEnabled: false } : {}) }
        if (recovered) patch.mediaRetryToken = prev.mediaRetryToken + 1
        return patch
      }),
    setVideoQuality: (videoQuality) => set({ videoQuality }),
    setScreenShareFrameRate: (screenShareFrameRate) => set({ screenShareFrameRate }),
    setWebcamFrameRate: (webcamFrameRate) => set({ webcamFrameRate }),
    setAspectRatio: (aspectRatio) => set({ aspectRatio }),
    setNoiseCancellation: (noiseCancellation) =>
      set((prev) => ({
        noiseCancellation,
        audioPreset:
          matchAudioPreset(prev.audioProcessing, noiseCancellation, prev.audioFormat) ?? 'custom',
      })),
    setAudioProcessing: (patch) =>
      set((prev) => {
        const audioProcessing = { ...prev.audioProcessing, ...patch }
        return {
          audioProcessing,
          audioPreset:
            matchAudioPreset(audioProcessing, prev.noiseCancellation, prev.audioFormat) ?? 'custom',
        }
      }),
    setAudioFormat: (patch) =>
      set((prev) => {
        const audioFormat = { ...prev.audioFormat, ...patch }
        return {
          audioFormat,
          audioPreset:
            matchAudioPreset(prev.audioProcessing, prev.noiseCancellation, audioFormat) ?? 'custom',
        }
      }),
    /**
     * Apply a preset wholesale.
     *
     * Constraints and encoder settings move together on purpose — that is the
     * entire value of a preset over four separate switches. `opus` lands here
     * too, but only reaches the room on the next producer: `codecOptions` is
     * read once, when the producer is created.
     */
    selectAudioPreset: (name) => {
      const preset = AUDIO_PRESETS[name]
      set({
        audioPreset: name,
        audioProcessing: { ...preset.processing },
        noiseCancellation: preset.noiseCancellation,
        audioFormat: { ...preset.format },
        opus: { ...preset.opus },
      })
    },
    // Fills empty selections in, and repairs ones pointing at hardware that
    // is no longer here.
    //
    // Selections persist across sessions now, so a stored id routinely refers
    // to a headset that has since been unplugged. `getUserMedia` is asked with
    // `ideal`, so capture still succeeds on the default device — but the UI
    // would show no selection while quietly using something else, and the mic
    // and camera switch effects compare against that id, so a stale one could
    // fire a pointless switch on every render.
    setDefaultsIfEmpty: (devices) =>
      set((prev) => {
        const next: Partial<DevicesState> = {}
        const firstOf = (kind: MediaDeviceKind) =>
          devices.find((d) => d.kind === kind && d.deviceId)?.deviceId
        const stillPresent = (id: string, kind: MediaDeviceKind) =>
          !!id && devices.some((d) => d.kind === kind && d.deviceId === id)

        // Enumeration before permission returns entries with empty ids and no
        // labels; treating that as "everything vanished" would wipe a good
        // selection on every cold load.
        const known = devices.some((d) => !!d.deviceId)
        if (!known) return next

        if (!stillPresent(prev.selectedMicId, 'audioinput')) {
          const fallback = firstOf('audioinput')
          if (fallback) next.selectedMicId = fallback
        }
        if (!stillPresent(prev.selectedCamId, 'videoinput')) {
          const fallback = firstOf('videoinput')
          if (fallback) next.selectedCamId = fallback
        }
        if (!stillPresent(prev.selectedSpeakerId, 'audiooutput')) {
          const fallback = firstOf('audiooutput')
          if (fallback) {
            next.selectedSpeakerId = fallback
            // Same reason as in selectSpeaker: this path does not go through
            // the setter, and the helper would otherwise keep routing to a
            // device that is gone.
            setPreferredSpeaker(fallback)
          }
        }
        return next
      }),
  }),
    {
      name: 'inmeet_devices',
      // v1 turns noise cancellation on. The field shipped defaulting to false
      // while RNNoise did not exist yet, so anyone who opened the app in the
      // meantime has `false` persisted and a changed default would never reach
      // them — they would be told it is on by default and see it off.
      //
      // v2 adds the audio preset, the capture format and the Opus options. A v1
      // record has none of them, and the merge zustand does is shallow — a
      // missing `audioFormat` would arrive as `undefined` and be spread into
      // getUserMedia as nothing at all. So they are filled in explicitly, and
      // the preset is DERIVED from whatever processing settings the user
      // already had rather than assumed: someone who had turned echo
      // cancellation off must not be relabelled "Conference audio".
      version: 5,
      migrate: (persisted: unknown, from: number) => {
        if (!persisted || typeof persisted !== 'object') return persisted
        let next = persisted as Record<string, unknown>
        if (from < 1) next = { ...next, noiseCancellation: true }
        if (from < 2) {
          const preset = AUDIO_PRESETS[DEFAULT_AUDIO_PRESET]
          const processing = {
            ...preset.processing,
            ...((next.audioProcessing as Partial<AudioProcessing>) ?? {}),
          }
          const noiseCancellation =
            typeof next.noiseCancellation === 'boolean'
              ? next.noiseCancellation
              : preset.noiseCancellation
          const audioFormat = { ...preset.format }
          next = {
            ...next,
            audioProcessing: processing,
            noiseCancellation,
            audioFormat,
            opus: { ...preset.opus },
            audioPreset: matchAudioPreset(processing, noiseCancellation, audioFormat) ?? 'custom',
          }
        }
        // v3 renames two quality options. The labels moved up a rung when each
        // option was mapped onto its own simulcast profile, so a stored
        // 'HD (720p)' or 'Full HD (1080p)' is a key that no longer exists —
        // it would rehydrate as undefined and index VIDEO_QUALITY_PRESETS to
        // nothing, taking the capture constraints down with it.
        //
        // Remapped by CAPTURE, not by label position: whoever picked
        // 'HD (720p)' chose 1280x720 and still gets 1280x720, which is 'High'
        // now. Following the label would silently promote them to a 1920
        // capture they never asked for.
        if (from < 3) {
          const RENAMED: Record<string, VideoQuality> = {
            'HD (720p)': 'High',
            'Full HD (1080p)': 'HD (1080p)',
          }
          const stored = next.videoQuality as string | undefined
          if (stored && stored in RENAMED) {
            next = { ...next, videoQuality: RENAMED[stored] }
          } else if (stored && !(stored in VIDEO_QUALITY_PRESETS)) {
            // Any other unknown value (hand-edited storage, a build from a
            // branch) falls back to the default rather than to undefined.
            next = { ...next, videoQuality: INITIAL.videoQuality }
          }
        }
        // screenShareFrameRate is in partialize, so a stored 15 wins over the
        // new default of 30 for every browser that has ever opened the app —
        // zustand's merge is { ...currentState, ...persistedState } and the
        // persisted side wins. Raising the default alone was therefore a no-op
        // for existing users, which is exactly the population that matters.
        //
        // Only stored 15 is lifted. 5 and 60 are deliberate choices from the
        // Settings dropdown and are left alone; 15 could not have been a
        // deliberate choice, because until d6538b76 that dropdown wrote to
        // local component state and never reached this store — every stored 15
        // is the old default, not a preference.
        if (from < 4) {
          if (next.screenShareFrameRate === 15) {
            next = { ...next, screenShareFrameRate: 30 }
          }
        }
        // v5 turns RNNoise off on phones (see RNNOISE_BY_DEFAULT). Same trap as
        // v1 in reverse: every phone that has opened the app has `true`
        // persisted, and the persisted side wins the merge. A stored true cannot
        // be told apart from the old default, so it is reset once on a phone;
        // the switch in Settings turns it back on and that choice then sticks.
        if (from < 5 && isPhone() && next.noiseCancellation === true) {
          const processing = next.audioProcessing as AudioProcessing | undefined
          const audioFormat = next.audioFormat as AudioFormat | undefined
          next = {
            ...next,
            noiseCancellation: false,
            ...(processing && audioFormat
              ? { audioPreset: matchAudioPreset(processing, false, audioFormat) ?? 'custom' }
              : {}),
          }
        }
        return next
      },
      // Selections and capture preferences only. The permission fields describe
      // right now — the user can change them in browser settings between two
      // visits — and mic/cam enabled are a per-meeting decision: a stored
      // "camera off" would silently follow you into your next call.
      partialize: (state) => ({
        selectedMicId: state.selectedMicId,
        selectedCamId: state.selectedCamId,
        selectedSpeakerId: state.selectedSpeakerId,
        audioProcessing: state.audioProcessing,
        noiseCancellation: state.noiseCancellation,
        audioPreset: state.audioPreset,
        audioFormat: state.audioFormat,
        opus: state.opus,
        videoQuality: state.videoQuality,
        screenShareFrameRate: state.screenShareFrameRate,
        webcamFrameRate: state.webcamFrameRate,
        aspectRatio: state.aspectRatio,
      }),
      // The speaker helper lives outside React, so a restored selection has to
      // be pushed into it — otherwise sounds played before the user touches
      // anything would go to the default device.
      onRehydrateStorage: () => (state) => {
        if (state?.selectedSpeakerId) setPreferredSpeaker(state.selectedSpeakerId)
      },
    },
  ),
)
